/**
 * Etapa 2 do login: código do Google Authenticator ou de recuperação.
 *
 * Só apresentação; `verificar(codigo)` (do container MfaVerify) fala com o
 * backend e rejeita com a mensagem dele. Os dois modos mandam o mesmo campo
 * `codigo` — o backend tenta TOTP e cai no código de recuperação.
 *
 * Fora do design por decisão do dono: "Lembrar este dispositivo" não existe.
 */
import React, { useEffect, useRef, useState } from "react";

import { MensagemAviso, MensagemErro } from "./MensagemCampo";
import useSegundoAtual from "./useSegundoAtual";
import {
  DIGITOS_TOTP,
  codigoAcabando,
  codigoRecuperacaoParaEnvio,
  codigoRecuperacaoValido,
  deslocamentoDoAnel,
  formatarMinutosSegundos,
  mensagemDeFalhaDaVerificacao,
  normalizarCodigoTotp,
  normalizarDigitacaoRecuperacao,
  segundosAteProximoCodigo,
} from "./logica";
import { IconeCheck, IconeChave, IconeEscudo, IconeVoltar } from "./icones";

export const MODO_AUTENTICADOR = "otp";
export const MODO_RECUPERACAO = "rec";

const PARADO = "idle";
const VERIFICANDO = "busy";
const LIBERADO = "ok";

const ERRO_INCOMPLETO = "curto";
const ERRO_FORMATO = "formato";
const ERRO_SERVIDOR = "servidor";

// Pausa curta antes do envio automático: deixa a 6ª caixa aparecer preenchida.
const ATRASO_ENVIO_AUTOMATICO_MS = 160;
// A partir daqui avisa que a verificação (mfa_token de 5 min) está acabando.
const AVISO_EXPIRACAO_SEGUNDOS = 60;

function AnelDoCodigo() {
  const restante = segundosAteProximoCodigo(useSegundoAtual());
  return (
    <span className={`ttl${codigoAcabando(restante) ? " low" : ""}`} title="Tempo até o próximo código">
      <svg className="ring" width="20" height="20" viewBox="0 0 22 22" aria-hidden="true" focusable="false">
        <circle className="ring-bg" cx="11" cy="11" r="9" />
        <circle className="ring-fg" cx="11" cy="11" r="9" style={{ strokeDashoffset: deslocamentoDoAnel(restante) }} />
      </svg>
      {restante} s
    </span>
  );
}

function ConteudoDoBotao({ verificacao }) {
  if (verificacao === VERIFICANDO) {
    return (
      <>
        <span className="spin" aria-hidden="true" />
        Verificando…
      </>
    );
  }
  if (verificacao === LIBERADO) {
    return (
      <>
        <IconeCheck />
        Acesso liberado
      </>
    );
  }
  return "Verificar";
}

function CaixasDoCodigo({ codigo, verificacao, comErro, desabilitado, somenteLeitura, aoMudar }) {
  const [focado, setFocado] = useState(false);
  const classe = `code${comErro ? " shake" : ""}${verificacao === LIBERADO ? " okc" : ""}`;
  const atual = Math.min(codigo.length, DIGITOS_TOTP - 1);
  return (
    <div className={classe}>
      {Array.from({ length: DIGITOS_TOTP }, (_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={`cb${codigo[i] ? " fill" : ""}${focado && i === atual && verificacao === PARADO ? " cur" : ""}`}
        >
          {codigo[i] || ""}
        </span>
      ))}
      <input
        id="erp-otp"
        className="code-in"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={DIGITOS_TOTP}
        value={codigo}
        disabled={desabilitado}
        // readOnly (e não disabled) enquanto verifica: mantém o foco e o
        // teclado do celular aberto para digitar de novo se o código falhar.
        readOnly={somenteLeitura}
        // Foco inicial: no celular o teclado numérico já abre.
        autoFocus
        onChange={(e) => aoMudar(e.target.value)}
        onPaste={(e) => {
          // maxLength cortaria "123 456" colado em "123 45" antes do onChange.
          const colado = e.clipboardData?.getData("text");
          if (!colado) return;
          e.preventDefault();
          aoMudar(colado);
        }}
        onFocus={() => setFocado(true)}
        onBlur={() => setFocado(false)}
        aria-invalid={comErro || undefined}
        aria-describedby="erp-otp-dica"
      />
    </div>
  );
}

export default function PainelMfa({
  emailMascarado,
  onVoltar,
  verificar,
  expirado = false,
  segundosRestantes = Infinity,
  quemAjuda = "ao administrador da sua marcenaria",
}) {
  const [modo, setModo] = useState(MODO_AUTENTICADOR);
  const [codigo, setCodigo] = useState("");
  const [recuperacao, setRecuperacao] = useState("");
  const [erro, setErro] = useState(null);
  const [verificacao, setVerificacao] = useState(PARADO);
  const montado = useRef(true);
  const timerEnvio = useRef(0);
  // O envio automático roda num timer: precisa ler o valor ATUAL, não o da
  // renderização em que o timer foi agendado.
  const atual = useRef({ codigo: "", recuperacao: "", verificacao: PARADO, expirado });
  atual.current = { codigo, recuperacao, verificacao, expirado };

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
      window.clearTimeout(timerEnvio.current);
    };
  }, []);

  async function enviar(tipo) {
    const agora = atual.current;
    if (agora.verificacao !== PARADO || agora.expirado) return;
    if (tipo === MODO_AUTENTICADOR && agora.codigo.length < DIGITOS_TOTP) {
      setErro({ tipo: ERRO_INCOMPLETO });
      return;
    }
    if (tipo === MODO_RECUPERACAO && !codigoRecuperacaoValido(agora.recuperacao)) {
      setErro({ tipo: ERRO_FORMATO });
      return;
    }
    setErro(null);
    setVerificacao(VERIFICANDO);
    try {
      await verificar(tipo === MODO_AUTENTICADOR ? agora.codigo : codigoRecuperacaoParaEnvio(agora.recuperacao));
      if (montado.current) setVerificacao(LIBERADO);
    } catch (falha) {
      if (!montado.current) return;
      setVerificacao(PARADO);
      setErro({ tipo: ERRO_SERVIDOR, mensagem: mensagemDeFalhaDaVerificacao(falha) });
      if (tipo === MODO_AUTENTICADOR) setCodigo("");
    }
  }

  function aoMudarCodigo(valor) {
    const limpo = normalizarCodigoTotp(valor);
    setCodigo(limpo);
    setErro(null);
    window.clearTimeout(timerEnvio.current);
    if (limpo.length === DIGITOS_TOTP) {
      timerEnvio.current = window.setTimeout(() => enviar(MODO_AUTENTICADOR), ATRASO_ENVIO_AUTOMATICO_MS);
    }
  }

  function trocarModo(novo) {
    window.clearTimeout(timerEnvio.current);
    setModo(novo);
    setErro(null);
  }

  const ocupado = verificacao !== PARADO;
  const travado = expirado || ocupado;
  const erroServidor = erro?.tipo === ERRO_SERVIDOR ? erro.mensagem : "";

  return (
    <div className="pane mfa">
      <button className="back" type="button" onClick={onVoltar}>
        <IconeVoltar />
        Voltar
      </button>
      <div className="mfa-i">
        <IconeEscudo />
      </div>
      <div className="kicker">Verificação em duas etapas</div>
      <h1 className="a-h">Digite o código do autenticador</h1>
      <p className="a-p">
        Abra o <b>Google Authenticator</b> no celular e digite o código de 6 dígitos da conta <b>Pharu ERP</b> ·{" "}
        {emailMascarado}.
      </p>

      {expirado && (
        <MensagemErro>
          Tempo esgotado para verificar este código. Por segurança, ele expira em 5 minutos. Faça login novamente.
        </MensagemErro>
      )}
      {!expirado && segundosRestantes <= AVISO_EXPIRACAO_SEGUNDOS && (
        <MensagemAviso>Esta verificação expira em {formatarMinutosSegundos(segundosRestantes)}.</MensagemAviso>
      )}

      {modo === MODO_AUTENTICADOR ? (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              enviar(MODO_AUTENTICADOR);
            }}
            noValidate
          >
            <div className="otp-h">
              <label className="f-l" htmlFor="erp-otp">Código de 6 dígitos</label>
              <AnelDoCodigo />
            </div>
            <CaixasDoCodigo
              codigo={codigo}
              verificacao={verificacao}
              comErro={erro?.tipo === ERRO_SERVIDOR}
              desabilitado={expirado}
              somenteLeitura={ocupado}
              aoMudar={aoMudarCodigo}
            />
            {erro?.tipo === ERRO_INCOMPLETO && <MensagemErro>Digite os 6 dígitos do código.</MensagemErro>}
            {erroServidor && <MensagemErro>{erroServidor}</MensagemErro>}
            <p className="hint" id="erp-otp-dica">
              O código muda a cada 30 segundos. Você também pode colar o código aqui.
            </p>
            <button className={`e-btn${verificacao === LIBERADO ? " ok" : ""}`} type="submit" disabled={expirado}>
              <ConteudoDoBotao verificacao={verificacao} />
            </button>
          </form>
          <button className="lnk2" type="button" disabled={travado} onClick={() => trocarModo(MODO_RECUPERACAO)}>
            Usar um código de recuperação
          </button>
        </>
      ) : (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              enviar(MODO_RECUPERACAO);
            }}
            noValidate
          >
            <div className="f">
              <label className="f-l" htmlFor="erp-rec">Código de recuperação</label>
              <div className={`f-box mono${erro?.tipo === ERRO_FORMATO || erroServidor ? " bad" : ""}`}>
                <IconeChave />
                <input
                  id="erp-rec"
                  className="mono"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  autoCapitalize="characters"
                  maxLength={9}
                  placeholder="XXXX-XXXX"
                  value={recuperacao}
                  disabled={expirado}
                  readOnly={ocupado}
                  autoFocus
                  onChange={(e) => {
                    setRecuperacao(normalizarDigitacaoRecuperacao(e.target.value));
                    setErro(null);
                  }}
                  aria-invalid={Boolean(erro) || undefined}
                />
              </div>
              {erro?.tipo === ERRO_FORMATO && <MensagemErro>Use o formato XXXX-XXXX.</MensagemErro>}
              {erroServidor && <MensagemErro>{erroServidor}</MensagemErro>}
            </div>
            <p className="hint">Cada código de recuperação só pode ser usado uma vez.</p>
            <button className={`e-btn${verificacao === LIBERADO ? " ok" : ""}`} type="submit" disabled={expirado}>
              <ConteudoDoBotao verificacao={verificacao} />
            </button>
          </form>
          <button className="lnk2" type="button" disabled={travado} onClick={() => trocarModo(MODO_AUTENTICADOR)}>
            Usar o Google Authenticator
          </button>
        </>
      )}

      <p className="alt">
        Perdeu acesso ao autenticador? Peça {quemAjuda} para redefinir a verificação em duas etapas.
      </p>
    </div>
  );
}
